/**
 * PanelTabs — barre d'onglets en tête de panneau (icon-only, drag-and-drop).
 *
 * @remarks
 * Effets visuels portés exactement du V1 (`Panel.module.css`) :
 * - **Tab dragué** : `opacity 0.35`, `scale 0.92`, `pointer-events: none`.
 * - **Reorder target** : indicateur vertical 2 px bleu *avant* le tab (pseudo
 *   `::before`).
 * - **Drop zone slot** : zone dédiée avec chevron qui apparait dans l'autre
 *   panneau pendant un drag, animée :
 *   - `dropZoneFadeIn` au mount.
 *   - `dropZonePulse` (va-et-vient horizontal infini, 0.45s).
 *   - `dropZoneNudge` (intensifié au hover, scale + nudge).
 *
 * Le drag suit le pattern V1 : tracking manuel via `mousedown` avec un
 * threshold de 5 px avant déclenchement (évite les déclenchements accidentels
 * lors d'un simple clic).
 *
 * Clavier : `TabList` porte les flèches, Home et End (SAAS-1187). Le
 * déplacement d'un onglet vers l'autre panneau reste à la souris (SAAS-900).
 */

import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { TabConfig } from '@/app/registry';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { useUIEphemeralStore } from '@/app/stores/use-ui-ephemeral-store';
import { Icon, Tab, TabList } from '@/shared/ui';

interface Props {
  tabs: readonly TabConfig[];
  activeTabId: string | null;
  onTabClick: (id: string) => void;
  side: 'left' | 'right';
}

const DRAG_THRESHOLD_PX = 5;

export function PanelTabs({ tabs, activeTabId, onTabClick, side }: Props) {
  const draggingTab = useUIEphemeralStore((s) => s.draggingTab);
  const setDraggingTab = useUIEphemeralStore((s) => s.setDraggingTab);
  const moveTabToPanel = useLayoutStore((s) => s.moveTabToPanel);
  const reorderTabsInPanel = useLayoutStore((s) => s.reorderTabsInPanel);

  const [reorderTarget, setReorderTarget] = useState<string | null>(null);
  const [dropZoneHovered, setDropZoneHovered] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; tabId: string } | null>(null);

  // Drop zone visible quand un tab est draggué DEPUIS CE panneau : la flèche
  // apparait dans le panneau d'origine, drop dessus = transfert vers l'autre.
  const showDropZone = draggingTab !== null && draggingTab.sourcePanel === side;

  // Cleanup states quand le drag se termine.
  useEffect(() => {
    if (draggingTab === null) {
      setReorderTarget(null);
      setDropZoneHovered(false);
    }
  }, [draggingTab]);

  // Cleanup global : si le drag se termine ailleurs (sur le shell ou autre),
  // on réinitialise quand même.
  useEffect(() => {
    if (draggingTab === null) return;
    const onGlobalUp = (): void => {
      setDraggingTab(null);
    };
    document.addEventListener('mouseup', onGlobalUp);
    return () => {
      document.removeEventListener('mouseup', onGlobalUp);
    };
  }, [draggingTab, setDraggingTab]);

  // Onglet masqué courant (`hideInTabBar`) : le dernier ouvert par programme. Il
  // reste dans la barre quand on bascule sur un autre onglet, pour pouvoir y
  // revenir. Mis à jour pendant le rendu (pas d'effet) : pattern React « ajuster
  // un state quand une prop change ».
  const [currentHiddenTabId, setCurrentHiddenTabId] = useState<string | null>(null);
  const activeIsHidden = tabs.some((tab) => tab.id === activeTabId && tab.hideInTabBar);
  if (activeIsHidden && activeTabId !== currentHiddenTabId) {
    setCurrentHiddenTabId(activeTabId);
  }

  // Onglets réellement affichés dans la barre : un onglet `hideInTabBar` n'y
  // figure que s'il est l'onglet masqué courant ; `enabledWhen` masque dynamiquement.
  const visibleTabs = tabs.filter(
    (tab) => (!tab.hideInTabBar || tab.id === currentHiddenTabId) && tab.enabledWhen?.() !== false,
  );

  const handleMouseDown = (e: MouseEvent<HTMLButtonElement>, tabId: string): void => {
    // Pas de drag si un seul tab dans le panneau (rien à reorder/move).
    if (visibleTabs.length <= 1) return;
    e.preventDefault();
    dragStartRef.current = { x: e.clientX, y: e.clientY, tabId };

    const onMove = (me: globalThis.MouseEvent): void => {
      if (dragStartRef.current === null) return;
      const dx = me.clientX - dragStartRef.current.x;
      const dy = me.clientY - dragStartRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
        setDraggingTab({
          tabId: dragStartRef.current.tabId,
          sourcePanel: side,
          ghostX: me.clientX,
          ghostY: me.clientY,
        });
        dragStartRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      }
    };
    const onUp = (): void => {
      dragStartRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Pendant un drag actif, on suit le curseur pour mettre à jour le ghost
  // et on écoute Esc pour annuler.
  const updateDragGhost = useUIEphemeralStore((s) => s.updateDragGhost);
  useEffect(() => {
    if (draggingTab === null) return;
    const onMove = (e: globalThis.MouseEvent): void => {
      updateDragGhost(e.clientX, e.clientY);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDraggingTab(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('keydown', onKey);
    };
  }, [draggingTab, updateDragGhost, setDraggingTab]);

  const handleMouseUpOnTab = (targetTabId: string): void => {
    if (draggingTab === null) return;
    if (draggingTab.tabId === targetTabId) {
      setDraggingTab(null);
      return;
    }
    if (draggingTab.sourcePanel === side) {
      // Reorder dans le même panneau : insère le draggué avant la cible.
      const ids = tabs.map((t) => t.id);
      const fromIdx = ids.indexOf(draggingTab.tabId);
      const toIdx = ids.indexOf(targetTabId);
      if (fromIdx >= 0 && toIdx >= 0) {
        const next = [...ids];
        next.splice(fromIdx, 1);
        next.splice(toIdx, 0, draggingTab.tabId);
        reorderTabsInPanel(side, next);
      }
    }
    setDraggingTab(null);
  };

  const handleDropZoneMouseUp = (): void => {
    if (draggingTab === null) return;
    if (dropZoneHovered) {
      // La flèche apparait dans le panneau d'origine ; drop dessus = transfert
      // vers le panneau opposé.
      const opposite = side === 'left' ? 'right' : 'left';
      moveTabToPanel(draggingTab.tabId, opposite);
    }
    setDraggingTab(null);
  };

  if (visibleTabs.length === 0) return null;

  // Drop zone slot — emplacement dépend du côté du panneau.
  // - side='right' (drag from left) : à GAUCHE des tabs (avant)
  // - side='left' (drag from right) : à DROITE des tabs (après, ml-auto)
  const dropZoneSlot = showDropZone ? (
    <div
      onMouseEnter={() => {
        setDropZoneHovered(true);
      }}
      onMouseLeave={() => {
        setDropZoneHovered(false);
      }}
      onMouseUp={handleDropZoneMouseUp}
      className={[
        'inline-flex size-9 shrink-0 cursor-crosshair items-center justify-center rounded-sm transition-colors',
        dropZoneHovered ? 'text-primary-hover' : 'text-primary',
        side === 'left' ? 'ml-auto' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        animation: dropZoneHovered
          ? 'dropZoneNudge 0.32s ease-in-out infinite'
          : 'dropZoneFadeIn 0.15s ease forwards, dropZonePulse 0.45s ease-in-out 0.25s infinite',
      }}
      // Pas de `role="button"` : cette zone n'a ni `onClick`, ni `tabIndex`, ni
      // gestion clavier — elle ne réagit qu'au `mouseUp` d'un glissement en
      // cours. L'annoncer comme un bouton promettait un contrôle qu'aucun
      // clavier ne peut actionner, ce qui est pire que de ne rien annoncer.
      // Le déplacement de panneau au clavier reste à faire (SAAS-900).
      aria-hidden
    >
      {side === 'right' ? (
        <Icon icon={ChevronLeft} size="md" />
      ) : (
        <Icon icon={ChevronRight} size="md" />
      )}
    </div>
  ) : null;

  return (
    <TabList
      aria-label={side === 'left' ? 'Onglets du panneau gauche' : 'Onglets du panneau droit'}
      // Hauteur partagée avec les en-têtes du panel center (`--panel-header-height`)
      // → bordure basse alignée entre les panneaux.
      className="border-border flex h-(--panel-header-height) w-full shrink-0 items-center gap-1 border-b px-2"
    >
      {/* Drop zone à gauche pour le panel right */}
      {side === 'right' && dropZoneSlot}

      {visibleTabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        const disabled = false;
        const TabIcon = tab.icon;
        const isDragging = draggingTab?.tabId === tab.id;
        const isReorderTarget =
          draggingTab !== null &&
          !isDragging &&
          reorderTarget === tab.id &&
          draggingTab.sourcePanel === side; // reorder uniquement dans le même panneau
        const canDrag = visibleTabs.length > 1 && !disabled;

        return (
          <Tab
            key={tab.id}
            data-tour={`panel-tab-${tab.id}`}
            title={tab.label}
            aria-label={tab.label}
            selected={isActive}
            disabled={disabled}
            onClick={() => {
              if (!isDragging) onTabClick(tab.id);
            }}
            onMouseDown={(e) => {
              handleMouseDown(e, tab.id);
            }}
            onMouseUp={() => {
              handleMouseUpOnTab(tab.id);
            }}
            onMouseEnter={() => {
              if (draggingTab !== null && !isDragging) setReorderTarget(tab.id);
            }}
            onMouseLeave={() => {
              if (draggingTab !== null) setReorderTarget(null);
            }}
            style={
              isDragging
                ? { opacity: 0.35, transform: 'scale(0.92)', pointerEvents: 'none' }
                : canDrag
                  ? { cursor: 'grab' }
                  : undefined
            }
            className={[
              // Indicateur reorder : barre verticale 2px bleue avant le tab
              isReorderTarget
                ? 'before:bg-primary before:absolute before:top-[20%] before:left-[-3px] before:h-[60%] before:w-0.5 before:rounded-sm'
                : '',
              disabled ? 'cursor-not-allowed opacity-50' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {TabIcon && (
              <Icon icon={TabIcon} size="md" strokeWidth={isActive && !disabled ? 2.25 : 1.75} />
            )}
          </Tab>
        );
      })}

      {/* Drop zone à droite pour le panel left */}
      {side === 'left' && dropZoneSlot}
    </TabList>
  );
}
